// src/pages/Communications.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { auth, db, functions } from "../firebase";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const COPPER = "#B87333";

const TEMPLATE_LIBRARY = [
  {
    id: "tournament-reminder",
    icon: "⏰",
    name: "Tournament Reminder",
    description: "A polished reminder with registration instructions.",
    channels: ["sms"],
    audienceType: "sms_opted_in",
    title: "Tournament reminder",
    smsBody:
      "♠️ River Ranch Poker Club Reminder\n\n[Tournament Name]\n\n[Day, Date] • [Time]\n\n📍 [Location]\n\nBuy-In: $[Amount]\n\nReply REGISTER to reserve your seat.\n\nCash games immediately following the tournament.",
    emailSubject: "River Ranch Poker Club Tournament Reminder",
    emailBody:
      "River Ranch Poker Club Tournament Reminder\n\n[Tournament Name]\n\n[Day, Date] at [Time]\n[Location]\n\nBuy-In: $[Amount]\n\nReply REGISTER to reserve your seat.",
  },
  {
    id: "tournament-announcement",
    icon: "📣",
    name: "Tournament Announcement",
    description: "Announce a newly published tournament.",
    channels: ["sms", "email"],
    audienceType: "all_active",
    title: "Tournament announcement",
    smsBody:
      "♠️ New River Ranch Poker Club Tournament\n\n[Tournament Name]\n\n[Day, Date] • [Time]\n📍 [Location]\nBuy-In: $[Amount]\n\nRegistration is now open. Reply REGISTER to reserve your seat.",
    emailSubject: "New River Ranch Poker Club Tournament",
    emailBody:
      "A new River Ranch Poker Club tournament is open for registration.\n\n[Tournament Name]\n[Day, Date] at [Time]\n[Location]\nBuy-In: $[Amount]\n\nReply REGISTER to reserve your seat.",
  },
  {
    id: "cash-game-tonight",
    icon: "♠️",
    name: "Cash Game Tonight",
    description: "Fill seats for tonight's cash game.",
    channels: ["sms"],
    audienceType: "sms_opted_in",
    title: "Cash game tonight",
    smsBody:
      "♠️ River Ranch Poker Club Cash Game\n\nCash game tonight at [Time].\n\n📍 [Location]\n\nReply IN if you plan to play. No reply is needed if you cannot make it.",
    emailSubject: "Cash Game Tonight",
    emailBody:
      "River Ranch Poker Club is hosting a cash game tonight at [Time].\n\n[Location]\n\nReply IN if you plan to play.",
  },
  {
    id: "dealers-wanted",
    icon: "🎴",
    name: "Dealers Wanted",
    description: "Recruit side-gig dealer candidates.",
    channels: ["sms", "email"],
    audienceType: "all_active",
    title: "Dealers wanted",
    smsBody:
      "🎴 River Ranch Poker Club is looking for 3–5 dealer candidates to train for occasional paid events.\n\nGreat side gig. Poker experience is helpful, but training is available.\n\nText DEAL to (979) 378-5259 to apply.",
    emailSubject: "Dealers Wanted at River Ranch Poker Club",
    emailBody:
      "River Ranch Poker Club is looking for 3–5 dealer candidates to train for occasional paid events.\n\nThis is a flexible side-gig opportunity. Poker experience is helpful, but training is available.\n\nText DEAL to (979) 378-5259 to apply.",
  },
  {
    id: "event-cancelled",
    icon: "⚠️",
    name: "Event Update",
    description: "Send a cancellation, delay, or venue change.",
    channels: ["sms", "email"],
    audienceType: "all_active",
    title: "Important event update",
    smsBody:
      "⚠️ River Ranch Poker Club Update\n\n[Event Name] scheduled for [Date/Time] has been [cancelled/postponed/moved].\n\n[Additional details]\n\nWe apologize for the inconvenience.",
    emailSubject: "Important River Ranch Poker Club Event Update",
    emailBody:
      "Important River Ranch Poker Club Update\n\n[Event Name] scheduled for [Date/Time] has been [cancelled/postponed/moved].\n\n[Additional details]\n\nWe apologize for the inconvenience.",
  },
  {
    id: "general-update",
    icon: "📨",
    name: "General Club Update",
    description: "Start with a clean branded message.",
    channels: ["sms"],
    audienceType: "sms_opted_in",
    title: "Club update",
    smsBody: "♠️ River Ranch Poker Club\n\n",
    emailSubject: "River Ranch Poker Club Update",
    emailBody: "",
  },
];

const EMPTY_FORM = {
  title: "",
  channels: ["sms"],
  audienceType: "sms_opted_in",
  selectedUids: [],
  smsBody: "♠️ River Ranch Poker Club\n\n",
  emailSubject: "",
  emailBody: "",
};

function getTimestampMs(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatDateTime(value) {
  const ms = getTimestampMs(value);
  return ms ? new Date(ms).toLocaleString() : "—";
}

function audienceLabel(type) {
  return (
    {
      all_active: "All active members",
      sms_opted_in: "SMS opted-in members",
      email_available: "Members with email",
      selected_members: "Selected members",
    }[type] || type
  );
}

function statusTone(status) {
  if (status === "completed") return "border-green-500/30 bg-green-500/10 text-green-300";
  if (status === "partially_failed") return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  if (status === "failed") return "border-red-500/30 bg-red-500/10 text-red-300";
  return "border-zinc-700 bg-zinc-800 text-zinc-300";
}

function getSmsSegmentEstimate(text) {
  const body = String(text || "");
  if (!body) return 0;
  const hasUnicode = /[^\x00-\x7F]/.test(body);
  const singleLimit = hasUnicode ? 70 : 160;
  const multiLimit = hasUnicode ? 67 : 153;
  return body.length <= singleLimit ? 1 : Math.ceil(body.length / multiLimit);
}

export default function Communications() {
  const [activeTab, setActiveTab] = useState("broadcasts");
  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [members, setMembers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testState, setTestState] = useState(null);
  const [testApproved, setTestApproved] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [testRecipient, setTestRecipient] = useState({
    smsMode: "account",
    smsPhone: "",
    emailMode: "account",
    email: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const payload = useMemo(
    () => ({
      title: form.title.trim(),
      channels: form.channels,
      audience: {
        type: form.audienceType,
        selectedUids:
          form.audienceType === "selected_members" ? form.selectedUids : [],
      },
      smsBody: form.smsBody.trim(),
      emailSubject: form.emailSubject.trim(),
      emailBody: form.emailBody.trim(),
    }),
    [form]
  );

  const stats = useMemo(() => {
    const activeMembers = members.length;
    const smsOptedIn = members.filter(
      (member) =>
        Boolean(member.phone) &&
        (member.smsOptIn === true ||
          member.smsOptedIn === true ||
          member.marketingSmsOptIn === true)
    ).length;
    const emailEnabled = members.filter((member) => Boolean(member.email)).length;
    const sent = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.sentCount || 0),
      0
    );
    const failed = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.failedCount || 0),
      0
    );
    const deliveryRate =
      sent + failed > 0 ? Math.round((sent / (sent + failed)) * 1000) / 10 : null;

    return { activeMembers, smsOptedIn, emailEnabled, deliveryRate };
  }, [members, campaigns]);

  const recentCampaigns = useMemo(() => campaigns.slice(0, 5), [campaigns]);

  const invalidateTest = useCallback(() => {
    setPreview(null);
    setTestState(null);
    setTestApproved(false);
    setConfirmText("");
  }, []);

  const updateForm = useCallback(
    (patch) => {
      setForm((previous) => ({ ...previous, ...patch }));
      invalidateTest();
      setError("");
      setSuccess("");
    },
    [invalidateTest]
  );

  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const next = snapshot.docs
        .map((document) => ({ id: document.id, ...document.data() }))
        .filter(
          (member) =>
            String(member.status || "active").toLowerCase() === "active"
        )
        .sort((a, b) =>
          String(a.name || a.displayName || a.email || "").localeCompare(
            String(b.name || b.displayName || b.email || "")
          )
        );
      setMembers(next);
    } catch (loadError) {
      console.error("Failed loading members:", loadError);
      setError("Could not load members.");
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, "communicationCampaigns"),
          orderBy("createdAt", "desc")
        )
      );
      setCampaigns(
        snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }))
      );
    } catch (loadError) {
      console.error("Failed loading communication history:", loadError);
      setCampaigns([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
    loadCampaigns();
  }, [loadMembers, loadCampaigns]);

  const filteredMembers = useMemo(() => {
    const selectedSet = new Set(form.selectedUids);
    const search = memberSearch.trim().toLowerCase();

    const selected = members
      .filter((member) => selectedSet.has(member.id))
      .sort((a, b) =>
        String(a.name || a.displayName || a.email || "").localeCompare(
          String(b.name || b.displayName || b.email || "")
        )
      );

    const unselected = members
      .filter((member) => !selectedSet.has(member.id))
      .filter((member) => {
        if (!search) return true;
        return [member.name, member.displayName, member.email, member.phone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      })
      .sort((a, b) =>
        String(a.name || a.displayName || a.email || "").localeCompare(
          String(b.name || b.displayName || b.email || "")
        )
      );

    return [...selected, ...unselected];
  }, [members, memberSearch, form.selectedUids]);

  const selectedMembers = useMemo(() => {
    const selectedSet = new Set(form.selectedUids);
    return members
      .filter((member) => selectedSet.has(member.id))
      .sort((a, b) =>
        String(a.name || a.displayName || a.email || "").localeCompare(
          String(b.name || b.displayName || b.email || "")
        )
      );
  }, [members, form.selectedUids]);

  const openBlankComposer = () => {
    setForm(EMPTY_FORM);
    setTestRecipient({
      smsMode: "account",
      smsPhone: "",
      emailMode: "account",
      email: "",
    });
    invalidateTest();
    setError("");
    setSuccess("");
    setComposerOpen(true);
    setActiveTab("broadcasts");
  };

  const openTemplate = (template) => {
    setForm({
      title: template.title,
      channels: template.channels,
      audienceType: template.audienceType,
      selectedUids: [],
      smsBody: template.smsBody,
      emailSubject: template.emailSubject,
      emailBody: template.emailBody,
    });
    invalidateTest();
    setError("");
    setSuccess("");
    setComposerOpen(true);
    setActiveTab("broadcasts");
  };

  const closeComposer = () => {
    if (sendingCampaign || sendingTest || previewing) return;
    setComposerOpen(false);
    setShowConfirm(false);
    setConfirmText("");
  };

  const toggleChannel = (channel) => {
    const next = form.channels.includes(channel)
      ? form.channels.filter((item) => item !== channel)
      : [...form.channels, channel];
    updateForm({ channels: next });
  };

  const toggleSelectedMember = (uid) => {
    const selectedUids = form.selectedUids.includes(uid)
      ? form.selectedUids.filter((item) => item !== uid)
      : [...form.selectedUids, uid];
    updateForm({ selectedUids });
  };

  const validateLocally = () => {
    if (!payload.title) return "Campaign title is required.";
    if (!payload.channels.length) return "Select SMS, email, or both.";
    if (payload.channels.includes("sms") && !payload.smsBody) {
      return "SMS message is required.";
    }
    if (
      payload.channels.includes("email") &&
      (!payload.emailSubject || !payload.emailBody)
    ) {
      return "Email subject and message are required.";
    }
    if (
      payload.audience.type === "selected_members" &&
      !payload.audience.selectedUids.length
    ) {
      return "Select at least one member.";
    }
    return "";
  };

  const callFunction = async (name, data) => {
    const callable = httpsCallable(functions, name);
    return callable(data);
  };

  const handlePreview = async () => {
    const validationError = validateLocally();
    if (validationError) {
      setError(validationError);
      return;
    }

    setPreviewing(true);
    setError("");
    setSuccess("");

    try {
      const result = await callFunction("previewCommunicationAudience", payload);
      setPreview(result.data);
    } catch (previewError) {
      console.error("Preview failed:", previewError);
      setError(previewError?.message || "Could not preview this communication.");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    const validationError = validateLocally();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (
      payload.channels.includes("sms") &&
      testRecipient.smsMode === "custom" &&
      !testRecipient.smsPhone.trim()
    ) {
      setError("Enter a custom phone number for the SMS test.");
      return;
    }

    if (
      payload.channels.includes("email") &&
      testRecipient.emailMode === "custom" &&
      !testRecipient.email.trim()
    ) {
      setError("Enter a custom email address for the email test.");
      return;
    }

    setSendingTest(true);
    setError("");
    setSuccess("");

    try {
      const previewResult =
        preview ||
        (await callFunction("previewCommunicationAudience", payload)).data;
      setPreview(previewResult);

      const result = await callFunction("sendCommunicationTest", {
        ...payload,
        testRecipient,
      });
      setTestState(result.data);
      setTestApproved(false);

      const destinations = [
        result.data?.testResults?.sms?.to,
        result.data?.testResults?.email?.to,
      ].filter(Boolean);

      setSuccess(
        destinations.length
          ? `Test sent to ${destinations.join(" and ")}. Review the exact message before approving.`
          : "Test sent. Review the exact message before approving."
      );
    } catch (sendError) {
      console.error("Test send failed:", sendError);
      setError(sendError?.message || "Could not send the test.");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!testState?.testId || !testApproved || confirmText !== "SEND") return;

    setSendingCampaign(true);
    setError("");
    setSuccess("");

    try {
      const result = await callFunction("sendCommunicationCampaign", {
        ...payload,
        testId: testState.testId,
        confirmation: confirmText,
      });

      setSuccess(
        `Campaign complete: ${result.data.sentCount} sent, ${result.data.failedCount} failed, ${result.data.skippedCount} skipped.`
      );
      setShowConfirm(false);
      setConfirmText("");
      setTestApproved(false);
      setTestState(null);
      setComposerOpen(false);
      await loadCampaigns();
      setActiveTab("history");
    } catch (sendError) {
      console.error("Campaign send failed:", sendError);
      setError(sendError?.message || "Could not send the campaign.");
    } finally {
      setSendingCampaign(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(circle at top right, rgba(184,115,51,0.22), transparent 35%), radial-gradient(circle at bottom left, rgba(184,115,51,0.10), transparent 28%)",
            }}
          />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#B87333]">
                River Ranch Poker Club
              </div>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-zinc-100 sm:text-4xl">
                Communications
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400 sm:text-base">
                Reach members quickly with polished club announcements, tournament
                reminders, cash-game invites, dealer recruiting, and important updates.
              </p>
            </div>
            <button
              type="button"
              onClick={openBlankComposer}
              className="inline-flex items-center justify-center rounded-2xl px-5 py-3.5 text-sm font-black text-zinc-950 shadow-lg transition hover:-translate-y-0.5"
              style={{
                background: COPPER,
                boxShadow: "0 14px 30px rgba(184,115,51,0.22)",
              }}
            >
              + New Broadcast
            </button>
          </div>
        </section>

        <div className="flex gap-2 overflow-x-auto border-b border-zinc-800">
          {[
            ["broadcasts", "Broadcasts"],
            ["history", "History"],
            ["templates", "Templates"],
            ["scheduled", "Scheduled"],
          ].map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-bold transition ${
                activeTab === tab
                  ? "border-[#B87333] text-[#B87333]"
                  : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-200">
            {success}
          </div>
        ) : null}

        {activeTab === "broadcasts" && !composerOpen ? (
          <>
            <section>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-xl font-black text-zinc-100">What are you sending today?</h2>
                  <p className="mt-1 text-sm text-zinc-500">
                    Start with a proven club template or create a custom broadcast.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {TEMPLATE_LIBRARY.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => openTemplate(template)}
                    className="group rounded-2xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:-translate-y-0.5 hover:border-[rgba(184,115,51,0.55)] hover:bg-zinc-900/90"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-zinc-700 bg-zinc-950 text-2xl">
                        {template.icon}
                      </div>
                      <span className="text-zinc-600 transition group-hover:text-[#B87333]">→</span>
                    </div>
                    <h3 className="mt-5 text-base font-black text-zinc-100">{template.name}</h3>
                    <p className="mt-2 text-sm leading-6 text-zinc-500">{template.description}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {template.channels.map((channel) => (
                        <span
                          key={channel}
                          className="rounded-full border border-zinc-700 bg-zinc-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-zinc-400"
                        >
                          {channel}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.65fr)]">
              <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-black text-zinc-100">Recent broadcasts</h2>
                    <p className="mt-1 text-sm text-zinc-500">Your latest club communications.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab("history")}
                    className="text-sm font-bold text-[#B87333]"
                  >
                    View all
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {loadingHistory ? (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5 text-sm text-zinc-500">
                      Loading recent broadcasts...
                    </div>
                  ) : null}

                  {!loadingHistory && recentCampaigns.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center">
                      <div className="text-3xl">📨</div>
                      <div className="mt-3 font-bold text-zinc-200">No broadcasts yet</div>
                      <p className="mt-1 text-sm text-zinc-500">
                        Your first completed campaign will appear here.
                      </p>
                    </div>
                  ) : null}

                  {recentCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-950 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="font-bold text-zinc-100">
                          {campaign.title || "Communication"}
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">
                          {formatDateTime(campaign.createdAt)} •{" "}
                          {(campaign.channels || [])
                            .map((channel) => channel.toUpperCase())
                            .join(" + ")}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-sm font-black text-zinc-100">
                            {campaign.sentCount || 0} sent
                          </div>
                          <div className="text-xs text-zinc-500">
                            {campaign.failedCount || 0} failed
                          </div>
                        </div>
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold capitalize ${statusTone(
                            campaign.status
                          )}`}
                        >
                          {String(campaign.status || "unknown").replaceAll("_", " ")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
                <h2 className="text-xl font-black text-zinc-100">Communication health</h2>
                <p className="mt-1 text-sm text-zinc-500">
                  A quick snapshot of your reachable audience.
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <Metric label="Active members" value={loadingMembers ? "—" : stats.activeMembers} />
                  <Metric label="SMS opt-in" value={loadingMembers ? "—" : stats.smsOptedIn} />
                  <Metric label="Email enabled" value={loadingMembers ? "—" : stats.emailEnabled} />
                  <Metric
                    label="Delivery rate"
                    value={stats.deliveryRate === null ? "—" : `${stats.deliveryRate}%`}
                  />
                </div>

                <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-950 p-4">
                  <div className="text-xs font-black uppercase tracking-wider text-zinc-500">
                    Safety workflow
                  </div>
                  <div className="mt-3 space-y-3 text-sm text-zinc-300">
                    <SafetyStep number="1" text="Preview the exact audience." />
                    <SafetyStep number="2" text="Send and review a personal test." />
                    <SafetyStep number="3" text="Approve and type SEND." />
                  </div>
                </div>
              </section>
            </div>
          </>
        ) : null}

        {activeTab === "broadcasts" && composerOpen ? (
          <Composer
            form={form}
            members={members}
            selectedMembers={selectedMembers}
            filteredMembers={filteredMembers}
            loadingMembers={loadingMembers}
            memberSearch={memberSearch}
            setMemberSearch={setMemberSearch}
            testRecipient={testRecipient}
            setTestRecipient={setTestRecipient}
            updateForm={updateForm}
            toggleChannel={toggleChannel}
            toggleSelectedMember={toggleSelectedMember}
            preview={preview}
            previewing={previewing}
            sendingTest={sendingTest}
            testState={testState}
            testApproved={testApproved}
            setTestApproved={setTestApproved}
            handlePreview={handlePreview}
            handleSendTest={handleSendTest}
            openConfirm={() => setShowConfirm(true)}
            closeComposer={closeComposer}
          />
        ) : null}

        {activeTab === "history" ? (
          <History campaigns={campaigns} loading={loadingHistory} refresh={loadCampaigns} />
        ) : null}

        {activeTab === "templates" ? (
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {TEMPLATE_LIBRARY.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => openTemplate(template)}
                className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:border-[rgba(184,115,51,0.55)]"
              >
                <div className="text-3xl">{template.icon}</div>
                <h3 className="mt-4 text-lg font-black text-zinc-100">{template.name}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-500">{template.description}</p>
                <div className="mt-5 text-sm font-bold text-[#B87333]">Use template →</div>
              </button>
            ))}
          </section>
        ) : null}

        {activeTab === "scheduled" ? (
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center">
            <div className="text-4xl">📅</div>
            <h2 className="mt-4 text-xl font-black text-zinc-100">Scheduled broadcasts are next</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-zinc-500">
              This release keeps every send intentional and immediate. Scheduling can be added
              next without changing the composer or campaign history.
            </p>
          </section>
        ) : null}
      </div>

      {showConfirm ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-lg rounded-3xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/10 text-2xl">
              ⚠️
            </div>
            <h2 className="mt-4 text-xl font-black text-zinc-100">Send this broadcast?</h2>
            <p className="mt-3 text-sm leading-6 text-zinc-400">
              This will send to{" "}
              <strong className="text-zinc-100">
                {preview?.uniqueRecipientCount || 0} members
              </strong>
              {preview?.estimatedSmsSegments
                ? ` and use approximately ${preview.estimatedSmsSegments} SMS segments`
                : ""}
              . This action cannot be undone.
            </p>

            <label className="mt-5 block text-sm font-bold text-zinc-200">
              Type SEND to confirm
            </label>
            <input
              autoFocus
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value.toUpperCase())}
              className="mt-2 w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-zinc-100 outline-none focus:border-red-500"
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowConfirm(false);
                  setConfirmText("");
                }}
                className="rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-2.5 text-sm font-bold text-zinc-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={confirmText !== "SEND" || sendingCampaign}
                onClick={handleSendCampaign}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-black text-white disabled:opacity-40"
              >
                {sendingCampaign ? "Sending..." : "Send broadcast"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
}

function Composer({
  form,
  members,
  selectedMembers,
  filteredMembers,
  loadingMembers,
  memberSearch,
  setMemberSearch,
  testRecipient,
  setTestRecipient,
  updateForm,
  toggleChannel,
  toggleSelectedMember,
  preview,
  previewing,
  sendingTest,
  testState,
  testApproved,
  setTestApproved,
  handlePreview,
  handleSendTest,
  openConfirm,
  closeComposer,
}) {
  const smsSegments = getSmsSegmentEstimate(form.smsBody);

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-4 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.22em] text-[#B87333]">
            Broadcast composer
          </div>
          <h2 className="mt-1 text-2xl font-black text-zinc-100">
            {form.title || "New communication"}
          </h2>
        </div>
        <button
          type="button"
          onClick={closeComposer}
          className="rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-sm font-bold text-zinc-300"
        >
          ← Back to dashboard
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
        <div className="space-y-5 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <Field label="Campaign title">
            <input
              value={form.title}
              onChange={(event) => updateForm({ title: event.target.value })}
              placeholder="August tournament reminder"
              className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-zinc-100 outline-none focus:border-[#B87333]"
            />
          </Field>

          <Field label="Channels">
            <div className="flex flex-wrap gap-3">
              {["sms", "email"].map((channel) => (
                <button
                  key={channel}
                  type="button"
                  onClick={() => toggleChannel(channel)}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-black uppercase tracking-wide transition ${
                    form.channels.includes(channel)
                      ? "border-[#B87333] bg-[rgba(184,115,51,0.14)] text-[#B87333]"
                      : "border-zinc-700 bg-zinc-950 text-zinc-400"
                  }`}
                >
                  {form.channels.includes(channel) ? "✓ " : ""}
                  {channel}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Audience">
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["all_active", "All active members", "Every active club member"],
                ["sms_opted_in", "SMS opt-in", "Members eligible for texts"],
                ["email_available", "Email members", "Members with an email address"],
                ["selected_members", "Selected members", "Choose recipients manually"],
              ].map(([value, title, description]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateForm({ audienceType: value })}
                  className={`rounded-xl border p-4 text-left transition ${
                    form.audienceType === value
                      ? "border-[#B87333] bg-[rgba(184,115,51,0.10)]"
                      : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`h-4 w-4 rounded-full border ${
                        form.audienceType === value
                          ? "border-[#B87333] bg-[#B87333] shadow-[inset_0_0_0_3px_#18181b]"
                          : "border-zinc-600"
                      }`}
                    />
                    <div>
                      <div className="text-sm font-black text-zinc-100">{title}</div>
                      <div className="mt-1 text-xs text-zinc-500">{description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Field>

          {form.audienceType === "selected_members" ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-black text-zinc-200">Select members</div>
                  <div className="text-xs text-zinc-500">
                    {form.selectedUids.length} selected
                  </div>
                </div>
                <input
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                  placeholder="Search members"
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none sm:w-56"
                />
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    updateForm({
                      selectedUids: Array.from(
                        new Set([
                          ...form.selectedUids,
                          ...filteredMembers.map((member) => member.id),
                        ])
                      ),
                    })
                  }
                  className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs font-bold text-zinc-300 hover:border-[#B87333]"
                >
                  Select all visible
                </button>
                <button
                  type="button"
                  onClick={() => updateForm({ selectedUids: [] })}
                  disabled={!form.selectedUids.length}
                  className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs font-bold text-zinc-300 hover:border-red-500 disabled:opacity-40"
                >
                  Clear all
                </button>
              </div>

              {selectedMembers.length ? (
                <div className="mb-4 rounded-xl border border-[rgba(184,115,51,0.30)] bg-[rgba(184,115,51,0.08)] p-3">
                  <div className="mb-2 text-xs font-black uppercase tracking-wider text-[#B87333]">
                    Selected recipients
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => toggleSelectedMember(member.id)}
                        title="Remove recipient"
                        className="inline-flex items-center gap-2 rounded-full border border-[rgba(184,115,51,0.38)] bg-zinc-950 px-3 py-1.5 text-xs font-bold text-zinc-200 hover:border-red-500"
                      >
                        <span>
                          {member.name ||
                            member.displayName ||
                            member.email ||
                            "Member"}
                        </span>
                        <span className="text-zinc-500">×</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {loadingMembers ? (
                  <p className="text-sm text-zinc-500">Loading members...</p>
                ) : null}

                {filteredMembers.map((member, index) => {
                  const selected = form.selectedUids.includes(member.id);
                  const previousSelected =
                    index > 0 &&
                    form.selectedUids.includes(filteredMembers[index - 1].id);
                  const showDivider =
                    !selected &&
                    form.selectedUids.length > 0 &&
                    (index === 0 || previousSelected);

                  return (
                    <React.Fragment key={member.id}>
                      {showDivider ? (
                        <div className="flex items-center gap-3 py-1">
                          <div className="h-px flex-1 bg-zinc-800" />
                          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-600">
                            Available members
                          </span>
                          <div className="h-px flex-1 bg-zinc-800" />
                        </div>
                      ) : null}
                      <label
                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-3 transition ${
                          selected
                            ? "border-[rgba(184,115,51,0.48)] bg-[rgba(184,115,51,0.09)]"
                            : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                        }`}
                      >
                        <div>
                          <div className="text-sm font-bold text-zinc-200">
                            {member.name ||
                              member.displayName ||
                              member.email ||
                              "Member"}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {[member.email, member.phone].filter(Boolean).join(" • ")}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectedMember(member.id)}
                          className="h-4 w-4 accent-[#B87333]"
                        />
                      </label>
                    </React.Fragment>
                  );
                })}

                {!loadingMembers && !filteredMembers.length ? (
                  <p className="py-4 text-center text-sm text-zinc-500">
                    No members match your search.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {form.channels.includes("sms") ? (
            <Field
              label="SMS message"
              right={`${form.smsBody.length} characters • ${smsSegments} segment${
                smsSegments === 1 ? "" : "s"
              }`}
            >
              <textarea
                value={form.smsBody}
                onChange={(event) => updateForm({ smsBody: event.target.value })}
                rows={11}
                className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 font-mono text-sm leading-6 text-zinc-100 outline-none focus:border-[#B87333]"
              />
            </Field>
          ) : null}

          {form.channels.includes("email") ? (
            <div className="space-y-4">
              <Field label="Email subject">
                <input
                  value={form.emailSubject}
                  onChange={(event) =>
                    updateForm({ emailSubject: event.target.value })
                  }
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-zinc-100 outline-none focus:border-[#B87333]"
                />
              </Field>

              <Field label="Email message">
                <textarea
                  value={form.emailBody}
                  onChange={(event) => updateForm({ emailBody: event.target.value })}
                  rows={11}
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm leading-6 text-zinc-100 outline-none focus:border-[#B87333]"
                />
              </Field>
            </div>
          ) : null}

          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="font-black text-zinc-200">Test recipient</div>
            <p className="mt-1 text-xs leading-5 text-zinc-500">
              Test messages are identical to the live broadcast. Custom destinations are used
              only for this test and do not change any member account.
            </p>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {form.channels.includes("sms") ? (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
                  <div className="text-sm font-black text-zinc-100">SMS test</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[
                      ["account", "My admin account"],
                      ["custom", "Custom phone"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setTestRecipient((previous) => ({
                            ...previous,
                            smsMode: value,
                          }))
                        }
                        className={`rounded-lg border px-3 py-2 text-xs font-bold ${
                          testRecipient.smsMode === value
                            ? "border-[#B87333] bg-[rgba(184,115,51,0.12)] text-[#B87333]"
                            : "border-zinc-700 bg-zinc-950 text-zinc-400"
                        }`}
                      >
                        {testRecipient.smsMode === value ? "✓ " : ""}
                        {label}
                      </button>
                    ))}
                  </div>
                  {testRecipient.smsMode === "custom" ? (
                    <input
                      value={testRecipient.smsPhone}
                      onChange={(event) =>
                        setTestRecipient((previous) => ({
                          ...previous,
                          smsPhone: event.target.value,
                        }))
                      }
                      inputMode="tel"
                      placeholder="(979) 555-1234"
                      className="mt-3 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-[#B87333]"
                    />
                  ) : (
                    <p className="mt-3 text-xs text-zinc-500">
                      Uses the phone number stored on your admin account.
                    </p>
                  )}
                </div>
              ) : null}

              {form.channels.includes("email") ? (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
                  <div className="text-sm font-black text-zinc-100">Email test</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[
                      ["account", "My admin account"],
                      ["custom", "Custom email"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setTestRecipient((previous) => ({
                            ...previous,
                            emailMode: value,
                          }))
                        }
                        className={`rounded-lg border px-3 py-2 text-xs font-bold ${
                          testRecipient.emailMode === value
                            ? "border-[#B87333] bg-[rgba(184,115,51,0.12)] text-[#B87333]"
                            : "border-zinc-700 bg-zinc-950 text-zinc-400"
                        }`}
                      >
                        {testRecipient.emailMode === value ? "✓ " : ""}
                        {label}
                      </button>
                    ))}
                  </div>
                  {testRecipient.emailMode === "custom" ? (
                    <input
                      value={testRecipient.email}
                      onChange={(event) =>
                        setTestRecipient((previous) => ({
                          ...previous,
                          email: event.target.value,
                        }))
                      }
                      inputMode="email"
                      placeholder="you@example.com"
                      className="mt-3 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-[#B87333]"
                    />
                  ) : (
                    <p className="mt-3 text-xs text-zinc-500">
                      Uses the email address stored on your admin account.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              disabled={previewing}
              onClick={handlePreview}
              className="rounded-xl border border-zinc-700 bg-zinc-800 px-5 py-3 text-sm font-bold text-zinc-100 hover:bg-zinc-700 disabled:opacity-50"
            >
              {previewing ? "Calculating..." : "Preview audience"}
            </button>
            <button
              type="button"
              disabled={sendingTest}
              onClick={handleSendTest}
              className="rounded-xl px-5 py-3 text-sm font-black text-zinc-950 disabled:opacity-50"
              style={{ background: COPPER }}
            >
              {sendingTest ? "Sending test..." : "Send test"}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <h3 className="text-lg font-black text-zinc-100">Live preview</h3>
            <p className="mt-1 text-xs text-zinc-500">
              Audience: {audienceLabel(form.audienceType)}
            </p>

            {form.channels.includes("sms") ? (
              <div className="mt-4 rounded-2xl border border-zinc-700 bg-zinc-950 p-4">
                <div className="mb-3 text-xs font-black uppercase tracking-wider text-zinc-500">
                  SMS
                </div>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-zinc-200">
                  {form.smsBody || "Your SMS preview will appear here."}
                </pre>
              </div>
            ) : null}

            {form.channels.includes("email") ? (
              <div className="mt-4 rounded-2xl border border-zinc-700 bg-zinc-950 p-4">
                <div className="mb-3 text-xs font-black uppercase tracking-wider text-zinc-500">
                  Email
                </div>
                <div className="text-sm font-black text-zinc-100">
                  {form.emailSubject || "Email subject"}
                </div>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-300">
                  {form.emailBody || "Your email preview will appear here."}
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <h3 className="text-lg font-black text-zinc-100">Audience summary</h3>
            {preview ? (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Metric label="Unique members" value={preview.uniqueRecipientCount} />
                <Metric label="SMS recipients" value={preview.smsRecipientCount} />
                <Metric label="Email recipients" value={preview.emailRecipientCount} />
                <Metric label="SMS segments" value={preview.estimatedSmsSegments} />
              </div>
            ) : (
              <p className="mt-3 text-sm text-zinc-500">
                Preview the audience to calculate recipients.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <h3 className="text-lg font-black text-zinc-100">Test approval</h3>
            {!testState ? (
              <p className="mt-3 text-sm leading-6 text-zinc-500">
                Send a test before the broadcast button becomes available.
              </p>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-200">
                  Test sent successfully.
                </div>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-700 bg-zinc-950 p-4">
                  <input
                    type="checkbox"
                    checked={testApproved}
                    onChange={(event) => setTestApproved(event.target.checked)}
                    className="mt-1 h-4 w-4 accent-[#B87333]"
                  />
                  <span className="text-sm leading-6 text-zinc-300">
                    I reviewed the received test and approve this exact message and
                    audience.
                  </span>
                </label>
                <button
                  type="button"
                  disabled={!testApproved || !preview?.uniqueRecipientCount}
                  onClick={openConfirm}
                  className="w-full rounded-xl bg-red-600 px-5 py-3 text-sm font-black text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Send to {preview?.uniqueRecipientCount || 0} members
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function History({ campaigns, loading, refresh }) {
  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-zinc-100">Campaign history</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Every custom broadcast sent from Communications.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm font-bold text-zinc-200"
        >
          Refresh
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <p className="text-sm text-zinc-500">Loading history...</p>
        ) : null}

        {!loading && campaigns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center text-zinc-500">
            No campaigns have been sent yet.
          </div>
        ) : null}

        {campaigns.map((campaign) => (
          <div
            key={campaign.id}
            className="rounded-xl border border-zinc-800 bg-zinc-950 p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="font-black text-zinc-100">
                  {campaign.title || "Communication"}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  {formatDateTime(campaign.createdAt)} •{" "}
                  {campaign.createdByName || "Admin"}
                </div>
                <div className="mt-2 text-xs text-zinc-400">
                  {(campaign.channels || [])
                    .map((channel) => channel.toUpperCase())
                    .join(" + ")}{" "}
                  • {audienceLabel(campaign.audience?.type)}
                </div>
              </div>

              <span
                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold capitalize ${statusTone(
                  campaign.status
                )}`}
              >
                {String(campaign.status || "unknown").replaceAll("_", " ")}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric label="Eligible" value={campaign.eligibleRecipientCount || 0} compact />
              <Metric label="Sent" value={campaign.sentCount || 0} compact />
              <Metric label="Failed" value={campaign.failedCount || 0} compact />
              <Metric label="Skipped" value={campaign.skippedCount || 0} compact />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Field({ label, right, children }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label className="block text-sm font-black text-zinc-200">{label}</label>
        {right ? <span className="text-xs text-zinc-500">{right}</span> : null}
      </div>
      {children}
    </div>
  );
}

function Metric({ label, value, compact = false }) {
  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-950 ${
        compact ? "p-3" : "p-4"
      }`}
    >
      <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div
        className={`font-black text-zinc-100 ${
          compact ? "mt-1 text-lg" : "mt-2 text-2xl"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SafetyStep({ number, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[rgba(184,115,51,0.30)] bg-[rgba(184,115,51,0.10)] text-xs font-black text-[#B87333]">
        {number}
      </span>
      <span>{text}</span>
    </div>
  );
}
